import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Package,
  QrCode,
  Store,
  Warehouse,
  Bike,
  ShieldCheck,
  Activity,
  ArrowRight,
  Truck,
  CheckCircle2,
  Github,
  Mail,
  MapPin,
} from "lucide-react";
import ThemeToggle from "@/components/ThemeToggle";
import Logo from "@/components/Logo";
import { useSiteSettings } from "@/hooks/useSiteSettings";

const HERO_BG =
  "https://static.prod-images.emergentagent.com/jobs/641bb6aa-cf02-4790-9e0d-c49c3442ce20/images/35fdab9066acaf6c8cca9c7d03004a7e314f76f04ba7a58a407e4dc4f8d74f93.png";
const WAREHOUSE_IMG =
  "https://images.unsplash.com/photo-1645736315000-6f788915923b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwzfHxsb2dpc3RpY3MlMjB3YXJlaG91c2UlMjBtb2Rlcm58ZW58MHx8fHwxNzc5NzQyMDkxfDA&ixlib=rb-4.1.0&q=85";
const RIDER_IMG =
  "https://images.unsplash.com/photo-1617347454431-f49d7ff5c3b1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwyfHxkZWxpdmVyeSUyMHJpZGVyJTIwcGFja2FnZXxlbnwwfHx8fDE3Nzk3NDIwOTJ8MA&ixlib=rb-4.1.0&q=85";

const FEATURES = [
  { icon: QrCode, title: "QR Code Package Tracking", text: "Every package is bound to a unique QR. Scan to update status in seconds." },
  { icon: Store, title: "Store Owner Package Creation", text: "Owners create packages, generate QR labels and hand-off to riders." },
  { icon: Warehouse, title: "Warehouse Receive & Ship", text: "Confirm arrivals, batch outbound shipments, and reconcile inventory." },
  { icon: Bike, title: "Rider Pickup & Last-Mile", text: "Real-time pickup queues and optimized last-mile delivery handoff." },
  { icon: ShieldCheck, title: "Admin Approval System", text: "Vet store owners and riders before they go live on the network." },
  { icon: Activity, title: "Real-Time Status Monitoring", text: "Watch the entire fleet from a single command center dashboard." },
];

const STEPS = [
  { n: "01", title: "Store creates package", text: "Owner enters items, receiver info, and generates a QR-coded label." },
  { n: "02", title: "Rider picks up at store", text: "Assigned rider scans QR at pickup to mark the package picked up." },
  { n: "03", title: "Warehouse receives", text: "Scan at intake confirms package arrival at the warehouse hub." },
  { n: "04", title: "Warehouse ships out", text: "Dispatcher batches packages onto outbound vehicles." },
  { n: "05", title: "Last-mile pickup", text: "Final-mile rider scans and rolls out to the destination." },
  { n: "06", title: "Delivered to customer", text: "QR scan + signature confirms the chain is complete." },
];

export default function Landing() {
  const navigate = useNavigate();
  const { data: site } = useSiteSettings();

  return (
    <div className="min-h-screen bg-white dark:bg-slate-950 text-slate-900 dark:text-white overflow-x-hidden">
      {/* NAV */}
      <header className="fixed top-0 inset-x-0 z-50 glass border-b border-slate-200 dark:border-slate-800">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" data-testid="brand-link" className="flex items-center gap-2 group">
            <Logo className="h-11" />
          </Link>
          <nav className="hidden md:flex items-center gap-8 text-sm text-slate-600 dark:text-slate-400">
            <a href="#features" className="hover:text-slate-900 hover:dark:text-white transition-colors">Features</a>
            <a href="#how-it-works" className="hover:text-slate-900 hover:dark:text-white transition-colors">How it Works</a>
            <a href="#network" className="hover:text-slate-900 hover:dark:text-white transition-colors">Network</a>
          </nav>
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <Button
              data-testid="nav-login-btn"
              variant="ghost"
              className="text-slate-700 dark:text-slate-300 hover:text-slate-900 hover:dark:text-white hover:bg-slate-200 hover:dark:bg-slate-800"
              onClick={() => navigate("/login")}
            >
              Login
            </Button>
            <Button
              data-testid="nav-register-store-btn"
              className="hidden sm:inline-flex bg-cyan-400 text-slate-950 hover:bg-cyan-300 font-medium"
              onClick={() => navigate("/register/store-owner")}
            >
              Register as Store Owner
            </Button>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="relative pt-32 pb-24 sm:pt-40 sm:pb-32 px-6 lg:px-10">
        <div
          className="absolute inset-0 -z-10 opacity-50"
          style={{
            backgroundImage: `url(${HERO_BG})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 -z-10 bg-gradient-to-b from-slate-950/70 via-slate-950/85 to-slate-950" />
        <div className="absolute inset-0 -z-10 grain opacity-40" />

        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-cyan-500/30 bg-cyan-500/5 text-cyan-300 text-xs font-mono-tech tracking-wider uppercase mb-8">
              <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse" />
              Live Logistics OS · v1.0
            </div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tighter leading-[0.95]">
              Smart package tracking
              <br />
              <span className="text-cyan-400">from store to customer.</span>
            </h1>
            <p className="mt-6 text-lg text-slate-700 dark:text-slate-300 leading-relaxed max-w-2xl">
              DeliveryBoss is the command center for modern logistics — stores, warehouses, riders, and customers
              wired into one QR-driven network.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Button
                data-testid="hero-login-btn"
                size="lg"
                className="bg-cyan-400 text-slate-950 hover:bg-cyan-300 font-medium h-12 px-6"
                onClick={() => navigate("/login")}
              >
                Login
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button
                data-testid="hero-register-store-btn"
                size="lg"
                variant="outline"
                className="h-12 px-6 border-slate-300 dark:border-slate-700 bg-slate-100/50 dark:bg-slate-900/50 text-slate-900 dark:text-white hover:bg-slate-200 hover:dark:bg-slate-800 hover:text-slate-900 hover:dark:text-white"
                onClick={() => navigate("/register/store-owner")}
              >
                <Store className="w-4 h-4 mr-2" />
                Register as Store Owner
              </Button>
              <Button
                data-testid="hero-register-rider-btn"
                size="lg"
                variant="outline"
                className="h-12 px-6 border-slate-300 dark:border-slate-700 bg-slate-100/50 dark:bg-slate-900/50 text-slate-900 dark:text-white hover:bg-slate-200 hover:dark:bg-slate-800 hover:text-slate-900 hover:dark:text-white"
                onClick={() => alert("Rider registration opens in Phase 3.")}
              >
                <Bike className="w-4 h-4 mr-2" />
                Register as Rider
              </Button>
            </div>

            <div className="mt-14 grid grid-cols-2 sm:grid-cols-4 gap-6 max-w-2xl">
              {[
                { k: "Touchpoints", v: "7" },
                { k: "User Roles", v: "5" },
                { k: "Statuses Tracked", v: "6" },
                { k: "Uptime SLA", v: "99.9%" },
              ].map((s) => (
                <div key={s.k} className="border-l-2 border-cyan-400/60 pl-3">
                  <div className="font-display text-2xl font-bold">{s.v}</div>
                  <div className="text-[11px] uppercase tracking-[0.2em] text-slate-500 mt-1">{s.k}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section id="features" className="py-24 sm:py-32 px-6 lg:px-10 border-t border-slate-200 dark:border-slate-800">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
            <div>
              <div className="text-xs font-mono-tech uppercase tracking-[0.2em] text-cyan-400 mb-3">/ Features</div>
              <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tighter max-w-2xl">
                Everything you need to ship like a pro.
              </h2>
            </div>
            <p className="text-slate-600 dark:text-slate-400 max-w-md text-base leading-relaxed">
              A single source of truth across every actor in the chain — built for speed, scale, and trust.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 dark:bg-slate-800 border border-slate-200 dark:border-slate-800 rounded-lg overflow-hidden">
            {FEATURES.map((f, i) => (
              <div
                key={f.title}
                data-testid={`feature-card-${i}`}
                className="group bg-white dark:bg-slate-950 p-8 hover:bg-slate-100 hover:dark:bg-slate-900 transition-colors duration-200"
              >
                <div className="w-11 h-11 rounded-md border border-slate-200 dark:border-slate-800 bg-slate-100 dark:bg-slate-900 grid place-items-center mb-6 group-hover:border-cyan-500/40 group-hover:bg-cyan-500/5 transition-colors">
                  <f.icon className="w-5 h-5 text-cyan-400" strokeWidth={2} />
                </div>
                <h3 className="font-display text-xl font-semibold mb-2">{f.title}</h3>
                <p className="text-slate-600 dark:text-slate-400 text-sm leading-relaxed">{f.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how-it-works" className="py-24 sm:py-32 px-6 lg:px-10 border-t border-slate-200 dark:border-slate-800 relative">
        <div className="max-w-7xl mx-auto">
          <div className="mb-16">
            <div className="text-xs font-mono-tech uppercase tracking-[0.2em] text-cyan-400 mb-3">/ How it works</div>
            <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tighter max-w-2xl">
              Six scans. One complete journey.
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {STEPS.map((s, i) => (
              <div
                key={s.n}
                data-testid={`step-card-${i}`}
                className="relative border border-slate-200 dark:border-slate-800 rounded-lg p-6 bg-slate-100/40 dark:bg-slate-900/40 hover:border-slate-300 hover:dark:border-slate-700 transition-colors"
              >
                <div className="flex items-center justify-between mb-4">
                  <span className="font-mono-tech text-xs text-slate-500">STEP {s.n}</span>
                  <Truck className="w-4 h-4 text-slate-700" />
                </div>
                <h3 className="font-display text-lg font-semibold mb-2">{s.title}</h3>
                <p className="text-slate-600 dark:text-slate-400 text-sm leading-relaxed">{s.text}</p>
                <div className="absolute -bottom-px left-6 right-6 h-px bg-gradient-to-r from-transparent via-cyan-400/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* NETWORK / VISUAL */}
      <section id="network" className="py-24 sm:py-32 px-6 lg:px-10 border-t border-slate-200 dark:border-slate-800">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-10 items-center">
          <div className="relative aspect-[4/3] rounded-lg overflow-hidden border border-slate-200 dark:border-slate-800">
            <img src={WAREHOUSE_IMG} alt="Warehouse" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-tr from-slate-950 via-transparent to-transparent" />
            <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between text-xs font-mono-tech">
              <span className="text-cyan-400 uppercase tracking-wider">HUB-MNL-01</span>
              <span className="text-slate-700 dark:text-slate-300 flex items-center gap-1">
                <CheckCircle2 className="w-3 h-3 text-green-400" /> ONLINE
              </span>
            </div>
          </div>
          <div className="relative aspect-[4/3] rounded-lg overflow-hidden border border-slate-200 dark:border-slate-800">
            <img src={RIDER_IMG} alt="Rider" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-tr from-slate-950 via-transparent to-transparent" />
            <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between text-xs font-mono-tech">
              <span className="text-cyan-400 uppercase tracking-wider">FLEET-NCR</span>
              <span className="text-slate-700 dark:text-slate-300 flex items-center gap-1">
                <CheckCircle2 className="w-3 h-3 text-green-400" /> 142 ACTIVE
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-6 lg:px-10 border-t border-slate-200 dark:border-slate-800">
        <div className="max-w-5xl mx-auto rounded-lg bg-gradient-to-b from-slate-900 to-slate-950 p-10 sm:p-14 text-center relative overflow-hidden border border-slate-800 text-white">
          <div className="absolute inset-0 grain opacity-30 pointer-events-none" />
          <h2 className="font-display text-3xl sm:text-5xl font-bold tracking-tighter mb-4 text-white">
            Run your logistics like a control room.
          </h2>
          <p className="text-slate-300 max-w-xl mx-auto mb-8">
            Sign in to the admin command center and see every package in motion.
          </p>
          <Button
            data-testid="cta-login-btn"
            size="lg"
            className="bg-cyan-400 text-slate-950 hover:bg-cyan-300 font-medium h-12 px-8"
            onClick={() => navigate("/login")}
          >
            Launch Admin Console
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-slate-200 dark:border-slate-800 py-12 px-6 lg:px-10">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-4 gap-8">
          <div>
            <div className="mb-4">
              <Logo className="h-12" />
            </div>
            <p
              data-testid="footer-tagline"
              className="text-sm text-slate-600 dark:text-slate-400 max-w-xs"
            >
              {site.tagline}
            </p>
          </div>
          <div>
            <div className="text-xs font-mono-tech uppercase tracking-[0.2em] text-slate-500 mb-3">Contact</div>
            <ul className="space-y-2 text-sm text-slate-600 dark:text-slate-400">
              <li className="flex items-center gap-2" data-testid="footer-contact-email">
                <Mail className="w-3.5 h-3.5" /> {site.contact_email}
              </li>
              <li className="flex items-center gap-2" data-testid="footer-contact-address">
                <MapPin className="w-3.5 h-3.5" /> {site.contact_address}
              </li>
            </ul>
          </div>
          <div>
            <div className="text-xs font-mono-tech uppercase tracking-[0.2em] text-slate-500 mb-3">Legal</div>
            <ul className="space-y-2 text-sm text-slate-600 dark:text-slate-400">
              <li>
                <Link
                  to="/legal/privacy"
                  data-testid="footer-link-privacy"
                  className="hover:text-slate-900 hover:dark:text-white transition-colors"
                >
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link
                  to="/legal/terms"
                  data-testid="footer-link-terms"
                  className="hover:text-slate-900 hover:dark:text-white transition-colors"
                >
                  Terms of Service
                </Link>
              </li>
            </ul>
          </div>
          <div>
            <div className="text-xs font-mono-tech uppercase tracking-[0.2em] text-slate-500 mb-3">System</div>
            <ul className="space-y-2 text-sm text-slate-600 dark:text-slate-400">
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-green-400" /> All systems operational
              </li>
              <li>
                <a
                  href="#"
                  className="hover:text-slate-900 hover:dark:text-white inline-flex items-center gap-1"
                >
                  <Github className="w-3.5 h-3.5" /> Changelog
                </a>
              </li>
            </ul>
          </div>
        </div>
        <div className="max-w-7xl mx-auto mt-12 pt-6 border-t border-slate-200 dark:border-slate-800 flex flex-col sm:flex-row justify-between text-xs text-slate-500 gap-2">
          <div data-testid="footer-copyright">{site.copyright_text}</div>
          <div className="font-mono-tech" data-testid="footer-version">{site.version_label}</div>
        </div>
      </footer>
    </div>
  );
}
